import { type Bucket } from 'investments/common/entities'

import { httpClientBanking } from '~/domains/banking/shared/infra/http-client-banking'
import { type PaginatedResponse } from '~/domains/platform/infra/http/paginated-client'
import { useInfiniteQueryRequest } from '~/ui/hooks/utils'

export async function fetchBucketList({
  accountId,
  cursor,
  limit
}: {
  accountId: string
  cursor?: string
  limit: number
}) {
  const { data } = await httpClientBanking<PaginatedResponse<Bucket>>({
    url: `/investments/accounts/${accountId}/buckets`,
    params: { cursor, limit }
  })

  return data
}

export function useFetchBucketList({
  accountId,
  limit = 100,
  options
}: {
  accountId: string
  limit?: number
  options?: {
    excludeBucketsByManagerSlug?: string[]
    cacheTime?: number
    staleTime?: number
    suspense?: boolean
    enabled?: boolean
  }
}) {
  const {
    data,
    isLoading: isBucketListLoading,
    isFetchingNextPage: isFetchingNextPageBucketListPage,
    hasNextPage: hasNextBucketListPage = false,
    fetchNextPage: fetchNextBucketListPage,
    ...rest
  } = useInfiniteQueryRequest<PaginatedResponse<Bucket>, unknown, true>(
    ['useFetchBucketList', accountId, limit],
    ({ pageParam }) => fetchBucketList({ accountId, cursor: pageParam, limit }),
    {
      getNextPageParam: lastPage => lastPage.cursor?.after,
      cacheTime: options?.cacheTime,
      staleTime: options?.staleTime,
      suspense: options?.suspense,
      enabled: options?.enabled
    }
  )

  const rawBucketList = data?.pages.flatMap(page => page.data) ?? []
  const bucketListData = options?.excludeBucketsByManagerSlug
    ? rawBucketList.filter(bucket => {
        if (bucket.manager) {
          return !options.excludeBucketsByManagerSlug?.includes(bucket.manager.slug)
        }
        return true
      })
    : rawBucketList

  return {
    bucketListData,
    isBucketListLoading,
    isFetchingNextPageBucketListPage,
    hasNextBucketListPage,
    fetchNextBucketListPage,
    ...rest
  }
}
